import { useState, useEffect } from 'react';
import axios from 'axios';

import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  InputAdornment,
  OutlinedInput,
  ListItemText,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  IconButton,
  Chip,
  FormControlLabel
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// API base URL - adjust as needed for your environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ContractForm() {
  // State for available projects (will be fetched from API)
  const [availableProjects, setAvailableProjects] = useState([]);
  
  // State for showing the add project form
  const [showAddProject, setShowAddProject] = useState({});
  
  // Handle custom project input
  const [newProjectInput, setNewProjectInput] = useState({});

  // Add validation state
const [validationErrors, setValidationErrors] = useState({});
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: 'info',
    message: ''
  });
  
  // Array of company contracts
  const [contracts, setContracts] = useState([
    createEmptyContractData()
  ]);

  const [costCenters, setCostCenters] = useState([]);
  const [companyCountry, setCompanyCountry] = useState('India'); // Default to India
  const [nationalIdLabel, setNationalIdLabel] = useState('PAN / Aadhaar');
  const [currencySymbol, setCurrencySymbol] = useState('₹');
  const [bankCodeLabel, setBankCodeLabel] = useState('IFSC Code');

  // Function to create an empty contract data object
  function createEmptyContractData() {
    return {
      companyName: '',
      companyAddress: '',
      companyPAN: '',
      companyTAN: '',
      bankName: '',
      ifscCode: '',
      accountNumber: '',
      selectedProjects: [],
      customProjects: [], // New field for custom projects
      contractStartDate: '',
      contractEndDate: '',
      contractDescription: '',
      employmentType: 'Contractor',
      status: 'active',
      projectBudget: '',
      companyPhone: '',
      companyEmail: '',
      hourlyRate: '',
      approvedHours: '160',
      ndaDoc: null,
      costCenterId: ''
    };
  }

  // Fetch projects and cost centers on component mount
  useEffect(() => {
    fetchCompanyData();
    fetchProjects();
    fetchCostCenters();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.company_id) {
        const { data } = await axios.get(`${API_BASE_URL}/api/cmp/${payload.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (data.success && data.data?.country) {
          setCompanyCountry(data.data.country);
          if (data.data.nationalIdLabel) setNationalIdLabel(data.data.nationalIdLabel);
          if (data.data.currency_symbol) setCurrencySymbol(data.data.currency_symbol);
          if (data.data.bankCodeLabel) setBankCodeLabel(data.data.bankCodeLabel);
        }
      }
    } catch (error) {
      // console.error("Error fetching company country:", error);
    }
  };

  const fetchCostCenters = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/cost-centers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCostCenters(response.data.data || []);
      }
    } catch (error) {
      //console.error("Error fetching cost centers:", error);
    }
  };

  // Function to fetch projects from API
  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/addteam/project/manager-active`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      if (response.data) {
        setAvailableProjects(response.data);
      }
    } catch (error) {
      //console.error('Error fetching projects:', error);
      setAlert({
        open: true,
        severity: 'error',
        message: 'Failed to load projects. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle the add project form for a specific contract
  const toggleAddProject = (index) => {
    setShowAddProject(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
    
    // Reset the input field when toggling
    setNewProjectInput(prev => ({
      ...prev,
      [index]: ''
    }));
  };



// Add validation functions
const validatePAN = (pan) => {
  if (companyCountry.toLowerCase() !== 'india') return pan && pan.trim().length > 0;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
};

const validateTAN = (tan) => {
  if (companyCountry.toLowerCase() !== 'india') return true; // TAN is typically Indian
  if (!tan) return true; // Optional field
  const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;
  return tanRegex.test(tan);
};

const validateIFSC = (ifsc) => {
  if (companyCountry.toLowerCase() !== 'india') return ifsc && ifsc.trim().length > 0;
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  return ifscRegex.test(ifsc);
};

const validateAccountNumber = (accountNo) => {
  return /^[0-9]{9,18}$/.test(accountNo);
};

const validateBudget = (budget) => {
  return /^[0-9]+(\.[0-9]{1,2})?$/.test(budget) && parseFloat(budget) > 0;
};


  const [newProjectCostCenter, setNewProjectCostCenter] = useState({});

  // Function to add a custom project
  const addCustomProject = (index) => {
    if (!newProjectInput[index] || newProjectInput[index].trim() === '') return;
    
    const updatedContracts = [...contracts];
    const ccId = newProjectCostCenter[index] ? Number(newProjectCostCenter[index]) : null;
    
    // Add to custom projects array
    updatedContracts[index] = {
      ...updatedContracts[index],
      customProjects: [
        ...updatedContracts[index].customProjects,
        { name: newProjectInput[index].trim(), costCenterId: ccId }
      ]
    };
    
    setContracts(updatedContracts);
    
    // Reset input field
    setNewProjectInput(prev => ({
      ...prev,
      [index]: ''
    }));
    setNewProjectCostCenter(prev => ({
      ...prev,
      [index]: ''
    }));
    
    // Hide the add project form
    toggleAddProject(index);
  };

  // Function to remove a custom project
  const removeCustomProject = (contractIndex, projectIndex) => {
    const updatedContracts = [...contracts];
    
    updatedContracts[contractIndex] = {
      ...updatedContracts[contractIndex],
      customProjects: updatedContracts[contractIndex].customProjects.filter(
        (_, idx) => idx !== projectIndex
      )
    };
    
    setContracts(updatedContracts);
  };

  // Handle contract data changes
  const handleContractDateChange = (index, field, date) => {
    const updatedContracts = [...contracts];
    
    updatedContracts[index] = {
      ...updatedContracts[index],
      [field]: date
    };
    
    setContracts(updatedContracts);
  };




// Handle contract data changes
const handleContractDataChange = (index, e) => {
  const { name, value, type, checked, files } = e.target;
  const updatedContracts = [...contracts];
  
  let finalValue = value;
  if (name === 'companyPhone') {
    finalValue = (value || '').replace(/\D/g, '').slice(0, 15);
  } else if (type === 'checkbox') {
    finalValue = checked;
  } else if (type === 'file') {
    finalValue = files[0];
  }
  
  updatedContracts[index] = {
    ...updatedContracts[index],
    [name]: finalValue
  };
  
  setContracts(updatedContracts);

  // ADD THIS VALIDATION LOGIC HERE:
  // Validation for specific fields
  if (name === 'companyPAN') {
    const isValid = validatePAN(value);
    setValidationErrors(prev => ({
      ...prev,
      [`${name}-${index}`]: isValid ? '' : (companyCountry.toLowerCase() === 'india' ? 'Invalid PAN format (e.g., ABCDE1234F)' : 'Invalid format')
    }));
  }
  
  if (name === 'companyTAN') {
    const isValid = validateTAN(value);
    setValidationErrors(prev => ({
      ...prev,
      [`${name}-${index}`]: isValid ? '' : 'Invalid TAN format (e.g., ABCD12345E)'
    }));
  }
  
  if (name === 'ifscCode') {
    const isValid = validateIFSC(value);
    setValidationErrors(prev => ({
      ...prev,
      [`${name}-${index}`]: isValid ? '' : (companyCountry.toLowerCase() === 'india' ? 'Invalid IFSC format (e.g., SBIN0001234)' : `Invalid ${bankCodeLabel} format`)
    }));
  }
  
  if (name === 'accountNumber') {
    const isValid = validateAccountNumber(value);
    setValidationErrors(prev => ({
      ...prev,
      [`${name}-${index}`]: isValid ? '' : 'Account number must be 9-18 digits'
    }));
  }
  
  if (name === 'projectBudget') {
    const isValid = validateBudget(value);
    setValidationErrors(prev => ({
      ...prev,
      [`${name}-${index}`]: isValid ? '' : 'Enter valid budget amount (numbers only)'
    }));
  }
};


  // Handle contract project selection
  const handleContractProjectChange = (index, event) => {
    const { value } = event.target;
    const updatedContracts = [...contracts];
    
    // Filter out null values and ensure all values are valid
    const validProjectIds = value.filter(id => id !== null && id !== undefined);
    
    updatedContracts[index] = {
      ...updatedContracts[index],
      selectedProjects: validProjectIds
    };
    
    setContracts(updatedContracts);
  };

  // Add a new contract
  const addContract = () => {
    setContracts([...contracts, createEmptyContractData()]);
  };

  // Remove a contract
  const removeContract = (index) => {
    if (contracts.length > 1) {
      const updatedContracts = contracts.filter((_, i) => i !== index);
      setContracts(updatedContracts);
    }
  };

  // Close alert handler
  const handleAlertClose = () => {
    setAlert({
      ...alert,
      open: false
    });
  };

  // Submit form data to the API
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    for (let i = 0; i < contracts.length; i++) {
      const contract = contracts[i];
      
      if (!contract.companyName ||
          !contract.companyAddress ||
          !contract.companyPAN ||
          !contract.bankName ||
          !contract.ifscCode ||
          !contract.accountNumber ||
          !contract.contractStartDate ||
          !contract.contractEndDate ||
          !contract.projectBudget ||
          !contract.companyPhone ||
          !contract.companyEmail ||
          !contract.hourlyRate ||
          !contract.approvedHours ||
          (contract.selectedProjects.length === 0 && contract.customProjects.length === 0)) {
        setAlert({
          open: true,
          severity: 'error',
          message: `Please complete all required fields for Contract #${i + 1}. At least one project (selected or custom) is required.`
        });
        return;
      }

      if (!/^\d{10,15}$/.test(contract.companyPhone.toString().trim())) {
        setAlert({
          open: true,
          severity: 'error',
          message: `Company Phone for Contract #${i + 1} must contain only numeric digits (10 to 15 digits).`
        });
        return;
      }
    }
    
    try {
      const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading(true);
      
      // Send each contract to the API
      const results = await Promise.all(
        contracts.map(async (contract) => {
          const formData = new FormData();
          
          // Append all contract fields to FormData
          Object.keys(contract).forEach(key => {
            if (key === 'ndaDoc') {
              if (contract.ndaDoc) {
                formData.append('ndaDoc', contract.ndaDoc);
              }
            } else if (key === 'customProjects') {
              contract[key].forEach(item => {
                const itemVal = typeof item === 'object' && item !== null ? JSON.stringify(item) : item;
                formData.append(key, itemVal);
              });
            } else if (key === 'selectedProjects') {
              contract[key].forEach(item => {
                formData.append(key, item);
              });
            } else {
              const val = contract[key];
              formData.append(key, val !== null && val !== undefined ? val : '');
            }
          });

          const response = await axios.post(`${API_BASE_URL}/api/contract`, formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data"
              }
            }
          );
          return response.data;
        })
      );
      
      // Check if all contracts were successfully added
      const allSuccessful = results.every(result => result.success);
      
      if (allSuccessful) {
        setAlert({
          open: true,
          severity: 'success',
          message: `Successfully created ${contracts.length} contract(s).`
        });
        
        // Reset form after successful submission
        setContracts([createEmptyContractData()]);
      } else {
        // Find which ones failed
        const failedIndices = results
          .map((result, index) => (!result.success ? index + 1 : null))
          .filter(index => index !== null);
        
        throw new Error(`Failed to create contract(s) #${failedIndices.join(', ')}`);
      }
    } catch (error) {
      //console.error('Error submitting form:', error);
      setAlert({
        open: true,
        severity: 'error',
        message: error.message || 'Failed to submit the form. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // If data is loading, show loading indicator
  if (loading && availableProjects.length === 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="1400px" sx={{ py: 4 }}>
      {/* Alert for success/error messages */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleAlertClose} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
      
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4"  fontWeight="bold">
          Add Contractors/Consultant
        </Typography>
      </Paper>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 6 }}>
        {/* Contract Cards - One per company */}
        {contracts.map((contract, index) => (
          <Card 
            key={index} 
            elevation={3} 
            sx={{ 
              mb: 4, 
              position: 'relative',
              border: '2px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              overflow: 'visible'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                {contracts.length > 1 && (
                  <Button 
                    variant="text" 
                    color="error" 
                    onClick={() => removeContract(index)}
                    startIcon={<DeleteIcon />}
                    size="small"
                  >
                    Remove
                  </Button>
                )}
              </Box>

              {/* Company Information Section */}
              <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.primary">
               Contractor Company Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`companyName-${index}`}
                    name="companyName"
                    label="Company Name"
                    value={contract.companyName}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                    
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`companyAddress-${index}`}
                    name="companyAddress"
                    label="Company Address"
                    value={contract.companyAddress}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`companyPAN-${index}`}
                    name="companyPAN"
                    label={nationalIdLabel}
                    value={contract.companyPAN}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                    error={!!validationErrors[`companyPAN-${index}`]}
                    helperText={validationErrors[`companyPAN-${index}`]}
                  />
                </Grid>
                {companyCountry.toLowerCase() === 'india' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id={`companyTAN-${index}`}
                      name="companyTAN"
                      label="Company TAN"
                      value={contract.companyTAN}
                      onChange={(e) => handleContractDataChange(index, e)}
                      variant="outlined"
                      error={!!validationErrors[`companyTAN-${index}`]}
                      helperText={validationErrors[`companyTAN-${index}`]}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`companyPhone-${index}`}
                    name="companyPhone"
                    label="Company Phone"
                    value={contract.companyPhone}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`companyEmail-${index}`}
                    name="companyEmail"
                    label="Company Email"
                    type="email"
                    value={contract.companyEmail}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Financial Details Section */}
              <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                Financial Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    id={`bankName-${index}`}
                    name="bankName"
                    label="Bank Name"
                    value={contract.bankName}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    required
                    id={`ifscCode-${index}`}
                    name="ifscCode"
                    label={bankCodeLabel}
                    value={contract.ifscCode}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"  
                    error={!!validationErrors[`ifscCode-${index}`]}
                    helperText={validationErrors[`ifscCode-${index}`]}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    id={`accountNumber-${index}`}
                    name="accountNumber"
                    label="Account Number"
                    value={contract.accountNumber}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                    error={!!validationErrors[`accountNumber-${index}`]}
                    helperText={validationErrors[`accountNumber-${index}`]}
                    inputProps={{ 
                      min: 0,
                      step: 1,
                      onKeyDown: (e) => {
                        // Prevent entering negative sign, decimal point, and 'e'
                        if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
                          e.preventDefault();
                        }
                      }
                    }}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              {/* Company Projects Section */}
              <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                Company Projects
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id={`company-project-select-label-${index}`}>Selected Projects</InputLabel>
                    <Select
                      labelId={`company-project-select-label-${index}`}
                      id={`company-project-select-${index}`}
                      multiple
                      value={contract.selectedProjects}
                      onChange={(e) => handleContractProjectChange(index, e)}
                      input={<OutlinedInput label="Selected Projects" />}
                      renderValue={(selected) => {
                        // Only show valid project names
                        return selected
                          .filter(id => id !== null && id !== undefined)
                          .map(id => 
                            availableProjects.find(p => p.project_id === id)?.name || id
                          ).join(', ');
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 224
                          },
                        },
                      }}
                    >
                      {availableProjects.map((project) => (
                        <MenuItem key={project.project_id} value={project.project_id}>
                          <Checkbox checked={contract.selectedProjects.indexOf(project.project_id) > -1} />
                          <ListItemText primary={project.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary">
                      Select existing projects from the dropdown
                    </Typography>
                  </FormControl>
                </Grid>
                
                {/* Custom Projects Section */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Custom Projects
                    </Typography>
                    
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => toggleAddProject(index)}
                    >
                      {showAddProject[index] ? 'Cancel' : 'Add New Project'}
                    </Button>
                  </Box>
                  
                  {/* Custom Project Add Form */}
                  {showAddProject[index] && (
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          value={newProjectInput[index] || ''}
                          onChange={(e) => setNewProjectInput({
                            ...newProjectInput,
                            [index]: e.target.value
                          })}
                          placeholder="Enter new project name"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id={`custom-cc-label-${index}`}>Cost Center (Optional)</InputLabel>
                          <Select
                            labelId={`custom-cc-label-${index}`}
                            id={`custom-cc-select-${index}`}
                            value={newProjectCostCenter[index] || ''}
                            label="Cost Center (Optional)"
                            onChange={(e) => setNewProjectCostCenter({
                              ...newProjectCostCenter,
                              [index]: e.target.value
                            })}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {costCenters.map((cc) => (
                              <MenuItem key={cc.id} value={cc.id}>
                                {cc.name} ({cc.code})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          size="medium"
                          onClick={() => addCustomProject(index)}
                        >
                          Add
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                  
                  {/* Display Added Custom Projects */}
                  {contract.customProjects.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {contract.customProjects.map((project, projectIndex) => {
                          const projName = typeof project === 'object' ? project.name : project;
                          const ccObj = typeof project === 'object' && project.costCenterId ? costCenters.find(c => c.id === project.costCenterId) : null;
                          const displayLabel = ccObj ? `${projName} (${ccObj.code})` : projName;
                          return (
                            <Chip
                              key={projectIndex}
                              label={displayLabel}
                              onDelete={() => removeCustomProject(index, projectIndex)}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    </Paper>
                  )}
                  
                  {/* Message when no projects selected */}
                  {contract.selectedProjects.length === 0 && contract.customProjects.length === 0 && (
                    <Typography variant="caption" color="error">
                      Please select existing projects or add at least one custom project
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Contract Details Section */}
              <Typography variant="h6" component="h3" fontWeight="medium" mb={2} color="text.secondary">
                Contract Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`contractStartDate-${index}`}
                    name="contractStartDate"
                    label="Contract Start Date"
                    type="date"
                    value={contract.contractStartDate || ''}
                    onChange={(e) => handleContractDateChange(index, 'contractStartDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`contractEndDate-${index}`}
                    name="contractEndDate"
                    label="Contract End Date"
                    type="date"
                    value={contract.contractEndDate || ''}
                    onChange={(e) => handleContractDateChange(index, 'contractEndDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id={`employmentType-label-${index}`}>Employment Type</InputLabel>
                    <Select
                      labelId={`employmentType-label-${index}`}
                      id={`employmentType-${index}`}
                      name="employmentType"
                      value={contract.employmentType}
                      onChange={(e) => handleContractDataChange(index, e)}
                      label="Employment Type"
                    >
                      <MenuItem value="Contractor">Contractor</MenuItem>
                      <MenuItem value="Consultant">Consultant</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id={`status-label-${index}`}>Status</InputLabel>
                    <Select
                      labelId={`status-label-${index}`}
                      id={`status-${index}`}
                      name="status"
                      value={contract.status}
                      onChange={(e) => handleContractDataChange(index, e)}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`projectBudget-${index}`}
                    name="projectBudget"
                    label="Project Budget"
                    type="number"
                    value={contract.projectBudget}
                    onChange={(e) => handleContractDataChange(index, e)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                    }}
                    variant="outlined"
                    error={!!validationErrors[`projectBudget-${index}`]}
  helperText={validationErrors[`projectBudget-${index}`]}
  inputProps={{ 
    min: 0,
    step: 0.01,
    onKeyDown: (e) => {
      // Prevent entering negative sign and 'e'
      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
        e.preventDefault();
      }
    }
  }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`hourlyRate-${index}`}
                    name="hourlyRate"
                    label={`Hourly Rate (${currencySymbol})`}
                    type="number"
                    value={contract.hourlyRate}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    id={`approvedHours-${index}`}
                    name="approvedHours"
                    label="Approved Hours"
                    type="number"
                    value={contract.approvedHours}
                    onChange={(e) => handleContractDataChange(index, e)}
                    variant="outlined"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Button
                      component="label"
                      variant="outlined"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      color={contract.ndaDoc ? "success" : "primary"}
                      sx={{ height: '56px' }}
                    >
                      {contract.ndaDoc ? "NDA Document Selected" : "Upload NDA Document"}
                      <input
                        type="file"
                        name="ndaDoc"
                        hidden
                        onChange={(e) => handleContractDataChange(index, e)}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                    </Button>
                    {contract.ndaDoc ? (
                      <Typography variant="caption" color="success.main" sx={{ mt: 1, pl: 1, display: 'block', fontWeight: 'medium' }}>
                        Selected File: {contract.ndaDoc.name}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.8, pl: 1, display: 'block', fontStyle: 'italic' }}>
                        * If the NDA is signed, please upload the document here.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id={`contractDescription-${index}`}
                    name="contractDescription"
                    label="Contract Description"
                    value={contract.contractDescription}
                    onChange={(e) => handleContractDataChange(index, e)}
                    multiline
                    rows={4}
                    variant="outlined"
                  />
                </Grid>
               
              </Grid>
            </CardContent>
          </Card>
        ))}
        
        {/* Add Another Contract Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddCircleIcon />}
            onClick={addContract}
            sx={{ mb: 2 }}
          >
            Add Another Contract
          </Button>
        </Box>
        
        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            disabled={loading}
            sx={{ minWidth: 200 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Save All Contracts'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}